import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar,
  Edit, 
  Trash2, 
  Clock, 
  ChevronLeft, 
  ChevronRight, 
  Settings, 
  AlertCircle ,
  X
} from 'lucide-react';
import { 
  updateClientStatus, 
  deleteClient,
  fetchClientSessions,
  supabase,
  addClientSession
} from '../../lib/supabase';
import StatusCompletionModal from './StatusCompletionModal';
import EditSessionModal from './EditSessionModal';
import { isDateTimeInPast } from '../../lib/utils/dateUtils';

const ListView = ({ clients, onViewDetails, onDelete, onEdit, navigate }) => {
  // Existing state variables
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);  
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isNewSessionModalOpen, setIsNewSessionModalOpen] = useState(false);
  const [clientForNewSession, setClientForNewSession] = useState(null);
  
  // New state variables for status completion modal
  const [isStatusCompletionModalOpen, setIsStatusCompletionModalOpen] = useState(false);
  const [selectedStatusClient, setSelectedStatusClient] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  
  // New state variable for client sessions
  const [clientSessions, setClientSessions] = useState({});
  
  // Pagination state variables
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showRowsSettings, setShowRowsSettings] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('');
  
  // Sort clients - Active sessions on top (sorted by earliest date), then completed clients
  const sortedClients = [...clients].sort((a, b) => {
    // First, separate by status
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    
    // For active clients with sessions, sort by next_session_date
    if (a.status !== 'completed' && b.status !== 'completed') {
      const aSession = clientSessions[a.id];
      const bSession = clientSessions[b.id];
      
      if (!aSession && !bSession) return 0;
      if (!aSession) return 1;
      if (!bSession) return -1;
      
      return new Date(aSession.session_date) - new Date(bSession.session_date);
    }
    
    // For completed clients, keep original order
    return 0;
  });

  // Fetch client sessions on component mount
  useEffect(() => {
    const loadClientSessions = async () => {
      const sessionsObj = {};
      
      await Promise.all(
        clients.map(async (client) => {
          try {
            const sessions = await fetchClientSessions(client.id);
            
            // Find the next upcoming session - use 'scheduled' instead of 'upcoming'
            const now = new Date();
            const upcomingSessions = sessions
              .filter(s => s.status === 'scheduled' && new Date(s.session_date) > now)
              .sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
            
            if (upcomingSessions.length > 0) {
              sessionsObj[client.id] = upcomingSessions[0];
            }
          } catch (err) {
            console.error(`Error fetching sessions for client ${client.id}:`, err);
          }
        })
      );
      
      setClientSessions(sessionsObj);
    };
    
    if (clients.length > 0) {
      loadClientSessions();
    }
  }, [clients]);

  const showToastNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const handleEditSession = async (clientId) => {
    try {
      // Get the client's next session
      const session = clientSessions[clientId];
      
      if (session) {
        // If there's an existing session, open the edit modal
        setSelectedSession(session);
        setIsEditSessionModalOpen(true);
      } else {
        // If no session, open the new session modal
        const client = clients.find(c => c.id === clientId);
        setClientForNewSession(client);
        setIsNewSessionModalOpen(true);
      }
    } catch (err) {
      console.error('Error preparing to edit session:', err);
    }
  };

  const NewSessionModal = ({ isOpen, onClose, client, onSessionAdded }) => {
    const [sessionData, setSessionData] = useState({
      title: '',
      date: new Date().toISOString().split('T')[0],
      time: '10:00',
      duration: 60,
      location: 'Online',
      notes: ''
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const handleChange = (e) => {
      const { name, value } = e.target;
      setSessionData(prev => ({
        ...prev,
        [name]: value
      }));
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!sessionData.title.trim()) {
        setError('Session title is required');
        return;
      }

      if (isDateTimeInPast(sessionData.date, sessionData.time)) {
        setError('Session time cannot be in the past');
        return;
      }
      
      setIsSubmitting(true);
      setError('');
      
      try {
        // Combine date and time
        const sessionDateTime = new Date(`${sessionData.date}T${sessionData.time}`);
        
        await addClientSession({
          client_id: client.id,
          title: sessionData.title,
          session_date: sessionDateTime.toISOString(),
          status: 'scheduled',
          duration_minutes: sessionData.duration,
          location: sessionData.location,
          notes: sessionData.notes
        });
        
        onSessionAdded();
        onClose();
      } catch (err) {
        console.error('Error adding session:', err);
        setError('Failed to add session: ' + (err.message || 'Unknown error'));
      } finally {
        setIsSubmitting(false);
      }
    };
    
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
          {/* Background overlay */}
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
  
          {/* Modal panel */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-teal-600 px-4 py-3 sm:px-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Schedule New Session for {client.name}</h3>
                <button
                  onClick={onClose}
                  className="text-white hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
  
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
              {error && (
                <div className="mb-4 p-2 bg-red-100 text-red-700 rounded flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  {error}
                </div>
              )}
  
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={sessionData.title}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="e.g., Initial Consultation, Follow-up Meeting"
                  />
                </div>
  
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={sessionData.date}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={sessionData.time}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
  
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes)
                    </label>
                    <select
                      name="duration"
                      value={sessionData.duration}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                      <option value="90">90 minutes</option>
                      <option value="120">120 minutes</option>
                    </select>
                  </div>
  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <select
                      name="location"
                      value={sessionData.location}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="Online">Online</option>
                      <option value="Office">Office</option>
                      <option value="Phone">Phone</option>
                      <option value="Client Location">Client Location</option>
                    </select>
                  </div>
                </div>
  
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    name="notes"
                    value={sessionData.notes}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    rows={3}
                    placeholder="Any additional details about this session"
                  />
                </div>
  
                <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm"
                  >
                    {isSubmitting ? 'Scheduling...' : 'Schedule Session'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Calculate pagination values
  const indexOfLastClient = currentPage * rowsPerPage;
  const indexOfFirstClient = indexOfLastClient - rowsPerPage;
  const currentClients = sortedClients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(sortedClients.length / rowsPerPage);

  const rowsSettingsRef = useRef(null);

  // Function to change page
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleCustomRowsSubmit = (e) => {
    e.preventDefault();
    const customValue = parseInt(customRowsInput, 10);
    
    // Validate input: must be a positive number between 1 and 100
    if (!isNaN(customValue) && customValue > 0 && customValue <= 100) {
      setRowsPerPage(customValue);
      setCurrentPage(1); // Reset to first page
      setShowRowsSettings(false);
      setCustomRowsInput(''); // Clear input
    } else {
      // Show error or simply ignore invalid input
      setCustomRowsInput(''); // Clear invalid input
    }
  };

  // Function to handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing rows per page
    setShowRowsSettings(false);
  };

  // Handle click outside for row settings dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showRowsSettings &&
        rowsSettingsRef.current && 
        !rowsSettingsRef.current.contains(event.target)
      ) {
        setShowRowsSettings(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRowsSettings]);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'on-hold', label: 'On Hold' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getClientTypeLabel = (type) => {
    const typeMap = {
      'below_8th': 'Below 8th Grade',
      '8th_10th': '8th to 10th Grade',
      'puc': 'PUC (11th & 12th)',
      'college': 'College Student',
      'professional': 'Professional'
    };
    return typeMap[type] || type;
  };

  const formatGradeYear = (gradeYear, clientType) => {
    if (!gradeYear) return '';
    
    // For school students, format as "Xth Grade" if it's just a number
    if (
      (clientType === 'below_8th' || clientType === '8th_10th' || clientType === 'puc') && 
      /^[0-9]+$/.test(gradeYear)
    ) {
      // Handle special cases for ordinal suffixes
      let suffix = "th";
      if (gradeYear.endsWith('1') && !gradeYear.endsWith('11')) suffix = "st";
      if (gradeYear.endsWith('2') && !gradeYear.endsWith('12')) suffix = "nd";
      if (gradeYear.endsWith('3') && !gradeYear.endsWith('13')) suffix = "rd";
      
      return `${gradeYear}${suffix} Grade`;
    }
    
    return gradeYear;
  };

  const handleDeleteConfirmation = (clientId, e) => {
    e.stopPropagation();
    setSelectedClientId(clientId);
    setConfirmOpen(true);
  };

  const handleEditClient = (client, e) => {
    e.stopPropagation();
    onEdit(client);
  };

  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    console.log(`Starting delete process for client ID: ${selectedClientId}`);
    
    try {
      // Call deleteClient with selected ID
      const result = await deleteClient(selectedClientId);
      console.log(`Delete operation returned:`, result);
      
      // Double-check the client was deleted by trying to fetch it
      const { data: checkClient, error: checkError } = await supabase
        .from('clients')
        .select('id')
        .eq('id', selectedClientId)
        .single();
      
      if (checkError && checkError.code === 'PGRST116') {
        // This error code typically means "no rows returned" which is good - client was deleted
        console.log(`Confirmed client ${selectedClientId} no longer exists in database`);
        onDelete(selectedClientId);
        setConfirmOpen(false);
        setSelectedClientId(null);
      } else if (checkClient) {
        // If we still found the client, deletion didn't work
        console.error(`Client ${selectedClientId} still exists after deletion attempt`);
        setDeleteError('Client still exists in database after deletion attempt. Please contact support.');
      } else {
        // Some other error occurred during check
        console.log(`Client check returned unexpected result:`, checkError);
        onDelete(selectedClientId);
        setConfirmOpen(false);
        setSelectedClientId(null);
      }
    } catch (err) {
      console.error('Error in delete process:', err);
      setDeleteError(`Delete failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Status change handler for the status completion modal
  const handleStatusChange = async (e, client) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    
    // Check if trying to set to 'completed'
    if (newStatus === 'completed') {
      // This is where you should add the check:
      const nextSession = clientSessions[client.id]; 
      if (nextSession) { 
        const sessionDate = new Date(nextSession.session_date); 
        const now = new Date(); 
        
        if (sessionDate > now) { 
          showToastNotification('Cannot mark as completed: scheduled session has not occurred yet'); 
          // Reset the select value back to the previous status
          e.target.value = client.status; 
          return; 
        } 
      }
      
      // Open the confirmation modal and set the pending status changes
      setSelectedStatusClient(client);
      setPendingStatusChange(newStatus);
      setIsStatusCompletionModalOpen(true);
      
      // Reset the select value back to the previous status until confirmed
      e.target.value = client.status;
      
      return;
    }
    
    // For non-completed statuses, apply the change immediately
    try {
      await updateClientStatus(client.id, newStatus);
      window.location.reload();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  // Handle status confirmation
  const handleStatusConfirm = async () => {
    try {
      await updateClientStatus(selectedStatusClient.id, pendingStatusChange);
      setIsStatusCompletionModalOpen(false);
      setSelectedStatusClient(null);
      setPendingStatusChange(null);
      
      // Refresh the client list after update
      window.location.reload();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg overflow-hidden border border-teal-400 shadow-md hover:shadow-lg transition-shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 border-b border-teal-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Registration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Next Session
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentClients.map((client) => {
              const isCompleted = client.status === 'completed';

              return (
                <tr 
                  key={client.id}
                  onClick={() => !isCompleted && onViewDetails(client.id)}
                  className={`${
                    isCompleted ? 'bg-gray-50 cursor-not-allowed' : 'hover:bg-teal-50 cursor-pointer'
                  } border-b border-teal-100 last:border-b-0 transition-colors`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className={`text-sm font-medium ${isCompleted ? 'text-gray-500' : 'text-gray-900'}`}>
                          {client.name}
                        </div>
                        <div className={`text-sm ${isCompleted ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatGradeYear(client.grade_year, client.type)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={client.status}
                      onChange={(e) => handleStatusChange(e, client)}
                      onClick={(e) => e.stopPropagation()}
                      className={`px-3 py-1 rounded-full text-sm ${getStatusColor(client.status)}`}
                    >
                      {statusOptions.map(option => (
                        <option 
                          key={option.value} 
                          value={option.value}
                          className="bg-white text-gray-700"
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getClientTypeLabel(client.type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm ${isCompleted ? 'text-gray-500' : 'text-gray-900'}`}>{client.email}</div>
                    <div className={`text-sm ${isCompleted ? 'text-gray-400' : 'text-gray-500'}`}>{client.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {client.registration_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {client.status !== 'cancelled' ? (
                        <>
                          {clientSessions[client.id] ? (
                            <div className="flex flex-col text-teal-600">
                              <span className="text-sm flex items-center">
                                <Calendar className="h-4 w-4 mr-2" />
                                {new Date(clientSessions[client.id].session_date).toLocaleDateString(undefined, {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              <span className="text-sm pl-6 text-gray-500">
                                {new Date(clientSessions[client.id].session_date).toLocaleTimeString(undefined, {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </span>
                              <span className="text-sm pl-6 text-teal-700 font-medium truncate max-w-[140px]">
                                {clientSessions[client.id].session_type}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">No session scheduled</span>
                          )}
                          {!isCompleted && (
                              <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSession(client.id);
                              }}
                              className="ml-2 p-1 text-teal-600 hover:text-teal-800 rounded-full hover:bg-gray-100"
                              title={clientSessions[client.id] ? "Edit Session" : "Manage Sessions"}
                            >
                              <Clock className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-gray-500 italic">N/A - Client cancelled</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center justify-center space-x-3">
                      {/* Edit button */}
                      {!isCompleted && (
                        <button
                          onClick={(e) => handleEditClient(client, e)}
                          className="text-teal-600 hover:text-teal-800 p-1 rounded-full hover:bg-teal-50"
                          title="Edit Client"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                      )}
                      
                      {/* Delete button */}
                      {!isCompleted && (
                        <button
                          onClick={(e) => handleDeleteConfirmation(client.id, e)}
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                          title="Delete Client"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {/* Pagination controls - moved inside the main table div */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-teal-200">
          <div className="flex items-center">
            <span className="text-sm text-gray-700">
              Showing <span className="font-medium">{indexOfFirstClient + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(indexOfLastClient, sortedClients.length)}
              </span>{' '}
              of <span className="font-medium">{sortedClients.length}</span> clients
            </span>
          </div>
          
          <div className="flex items-center">
            {/* Rows per page setting */}
            <div className="relative mr-4" ref={rowsSettingsRef}>
              <button
                onClick={() => setShowRowsSettings(!showRowsSettings)}
                className="flex items-center text-sm text-gray-700 px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                <Settings className="h-4 w-4 mr-1" />
                <span>{rowsPerPage} rows</span>
              </button>
              
              {showRowsSettings && (
                <div className="fixed mt-1 bg-white border-2 border-gray-300 rounded-md shadow-xl z-[100]" style={{ 
                  top: rowsSettingsRef.current?.getBoundingClientRect().bottom + window.scrollY + 5 + 'px',
                  left: rowsSettingsRef.current?.getBoundingClientRect().left + 'px',
                  minWidth: '160px'
                }}>
                  <div className="py-1">
                    {/* Preset options */}
                    {[5, 10, 20, 50].map((option) => (
                      <button
                        key={option}
                        onClick={() => handleRowsPerPageChange({ target: { value: option } })}
                        className={`block px-4 py-2 text-sm w-full text-left ${
                          rowsPerPage === option ? 'bg-teal-50 text-teal-700' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {option} rows
                      </button>
                    ))}
                    
                    {/* Divider */}
                    <div className="border-t border-gray-200 my-1"></div>
                    
                    {/* Custom input */}
                    <form onSubmit={handleCustomRowsSubmit} className="px-4 py-2">
                      <div className="flex items-center">
                        <input
                          type="number"
                          value={customRowsInput}
                          onChange={(e) => setCustomRowsInput(e.target.value)}
                          placeholder="Custom"
                          min="1"
                          max="100"
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded mr-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                        />
                        <button
                          type="submit"
                          className="px-2 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700"
                        >
                          Apply
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Max: 100 rows</div>
                    </form>
                  </div>
                </div>
              )}
            </div>
            
            {/* Pagination buttons */}
            <nav className="flex items-center">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-2 py-1 text-sm font-medium border border-gray-300 rounded-l ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              {/* Page numbers */}
              <div className="hidden sm:flex">
                {[...Array(totalPages).keys()].map((number) => {
                  // Show only 5 page numbers with current page in middle when possible
                  const pageNumber = number + 1;
                  const minVisiblePage = Math.max(1, currentPage - 2);
                  const maxVisiblePage = Math.min(totalPages, minVisiblePage + 4);
                  const adjustedMinVisiblePage = Math.max(1, maxVisiblePage - 4);
                  
                  if (
                    pageNumber >= adjustedMinVisiblePage &&
                    pageNumber <= maxVisiblePage
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`px-3 py-1 text-sm font-medium border-t border-b border-gray-300 ${
                          currentPage === pageNumber
                            ? 'bg-teal-50 text-teal-700 border-teal-300'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        } ${
                          pageNumber === adjustedMinVisiblePage ? '' : ''
                        } ${
                          pageNumber === maxVisiblePage ? '' : ''
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  return null;
                })}
              </div>
              
              {/* Mobile-friendly current page indicator */}
              <div className="flex sm:hidden items-center border-t border-b border-gray-300 px-3 py-1 text-sm font-medium bg-white">
                <span className="text-gray-700">
                  {currentPage} / {totalPages}
                </span>
              </div>
              
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-2 py-1 text-sm font-medium border border-gray-300 rounded-r ${
                  currentPage === totalPages || totalPages === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Overlay */}
          <div className="fixed inset-0 bg-black opacity-50"></div>

          {/* Modal Content */}
          <div className="bg-white p-6 rounded shadow-lg relative z-10 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to permanently delete this client?</p>
            
            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded">
                {deleteError}
              </div>
            )}
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setDeleteError(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className={`px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center ${
                  isDeleting ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Completion Modal */}
      {isStatusCompletionModalOpen && selectedStatusClient && (
        <StatusCompletionModal
          isOpen={isStatusCompletionModalOpen}
          onClose={() => {
            setIsStatusCompletionModalOpen(false);
            setSelectedStatusClient(null);
            setPendingStatusChange(null);
          }}
          onConfirm={handleStatusConfirm}
          clientName={selectedStatusClient.name}
        />
      )}

      {showToast && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in-down">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>{toastMessage}</span>
            </div>
          </div>
        </div>
      )}

{isEditSessionModalOpen && selectedSession && (
  <EditSessionModal
    isOpen={isEditSessionModalOpen}
    onClose={() => {
      setIsEditSessionModalOpen(false);
      setSelectedSession(null);
    }}
    session={selectedSession}
    onUpdate={() => {
      // Refresh the sessions data
      const loadClientSessions = async () => {
        const sessionsObj = {};
        
        await Promise.all(
          clients.map(async (client) => {
            try {
              const sessions = await fetchClientSessions(client.id);
              
              // Find the next upcoming session
              const now = new Date();
              const upcomingSessions = sessions
                .filter(s => s.status === 'scheduled' && new Date(s.session_date) > now)
                .sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
              
              if (upcomingSessions.length > 0) {
                sessionsObj[client.id] = upcomingSessions[0];
              }
            } catch (err) {
              console.error(`Error fetching sessions for client ${client.id}:`, err);
            }
          })
        );
        
        setClientSessions(sessionsObj);
      };
      
      loadClientSessions();
    }}
  />
)}
{isNewSessionModalOpen && clientForNewSession && (
  <NewSessionModal
    isOpen={isNewSessionModalOpen}
    onClose={() => {
      setIsNewSessionModalOpen(false);
      setClientForNewSession(null);
    }}
    client={clientForNewSession}
    onSessionAdded={() => {
      // Refresh the sessions data
      const loadClientSessions = async () => {
        const sessionsObj = {};
        
        await Promise.all(
          clients.map(async (client) => {
            try {
              const sessions = await fetchClientSessions(client.id);
              
              // Find the next upcoming session
              const now = new Date();
              const upcomingSessions = sessions
                .filter(s => s.status === 'scheduled' && new Date(s.session_date) > now)
                .sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
              
              if (upcomingSessions.length > 0) {
                sessionsObj[client.id] = upcomingSessions[0];
              }
            } catch (err) {
              console.error(`Error fetching sessions for client ${client.id}:`, err);
            }
          })
        );
        
        setClientSessions(sessionsObj);
      };
      
      loadClientSessions();
    }}
  />
)}
    </div>
  );
};

export default ListView;